import { Controller } from '@hotwired/stimulus'
// @ts-expect-error: @hotwired/turbo is not typed
import * as Turbo from '@hotwired/turbo'
// @ts-expect-error: el-transition is not typed
import { leave } from 'el-transition'

import { membersCards } from 'rails_routes'

// VGSCollect comes from https://js.verygoodvault.com/vgs-collect/2.18.5/vgs-collect.js
// which is loaded via `javascript_include_tag`, which makes it available as
// `window.VGSCollect`.
declare global {
  interface Window {
    VGSCollect: {
      create(vaultId: string, environment: string, stateCallback?: (state: any) => void): any;
    };
  }
}

// Connects to data-controller="vgs-collect-set-pin"
export default class extends Controller {
  static targets = [
    'body',
    'close',
  ]

  declare readonly bodyTarget: HTMLElement
  declare readonly closeTarget: HTMLElement

  static values = {
    cardId: String,
    cardRemoteId: String,
    customerToken: String,
    vgsEnv: String,
    vgsVaultId: String,
  }

  declare vgsVaultIdValue: string
  declare vgsEnvValue: string
  declare customerTokenValue: string
  declare cardIdValue: string
  declare cardRemoteIdValue: string

  connect () {
    // Note: We don't include a CSS transition for opening the set PIN modal because it's is most
    // likely done during the activation fow and in that case we don't want the screen to flicker
    // between the activation -> set PIN steps. However, this does mean that in the off chance the
    // PIN is not set during activation and the member tries to do it manually, the modal will not
    // have a nice transition during open and it'll be a very sudden open.

    const vgsVaultId = this.vgsVaultIdValue
    const vgsEnv = this.vgsEnvValue
    const customerToken = this.customerTokenValue
    const cardId = this.cardIdValue
    const cardRemoteId = this.cardRemoteIdValue

    document.body.classList.add('modal-open') // Locks scrolling

    const form = window.VGSCollect.create(vgsVaultId, vgsEnv, function (state: any) {
      if (state) {
        for (const field in state) {
          const span: HTMLElement = document.querySelector(`[data-name='${field}']`)!
          const frame = span.previousElementSibling!
          if (
            state[field].errorMessages &&
            !state[field].isValid &&
            !state[field].isFocused &&
            state[field].isDirty
          ) {
            const error = state[field].errorMessages[0]
            span.innerText = error
            if (!frame.classList.contains('invalid-field')) {
              frame.classList.add('invalid-field')
            }
          } else if (state[field].isValid || state[field].isFocused) {
            span.innerText = ''
            frame.classList.remove('invalid-field')
          }
        }
      }
    })

    form.field('#cc-pin', {
      type: 'text',
      name: 'data.attributes.pin',
      successColor: '#4F8A10',
      errorColor: '#D8000C',
      placeholder: '1234',
      maxLength: 6,
      validations: ['required', '/^([0-9]{4,6})$/'],
      autoFocus: true,
      css: {
        'font-family': 'Inter var, ui-sans-serif, system-ui',
        'font-size': '14px',
        display: 'block',
        width: '94.5%',
        height: '90%',
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        appearance: 'none',
        border: '1px solid transparent',
        'border-radius': '0.375rem',
        'padding-left': '10px',
        filter: 'drop-shadow(0 1px 1px rgb(0 0 0 / 0.05))',
        '&:focus': {
          'border-width': '2px',
          'border-color': '#6366f1',
          outline: '2px solid transparent',
          'outline-offset': '2px',
        },
      },
    })

    const highlightErrors = function (targetForm: HTMLFormElement, errors: any[]) {
      targetForm
        .querySelectorAll('.vgs-collect-container__invalid')
        .forEach(e => e.classList.add('invalid-field'))
      for (const error in errors) {
        const errorMessage = errors[error].errorMessages[0]
        const span = targetForm.querySelector('[data-name="' + error + '"]')! as HTMLSpanElement
        if (!span.innerText) {
          span.innerText = errorMessage
        }
      }

      enableSubmit()
    }

    const submitForm = function (form: any, targetForm: HTMLFormElement) {
      form.submit(
        '/cards/' + cardRemoteId + '/secure-data/pin',
        {
          // This converts the dot-separated field name strings into a JSON object
          mapDotToObject: 'true',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Authorization: 'Bearer ' + customerToken,
          },
        },
        function (status: number, data: any) {
          if (status >= 200 && status < 300) {
            // Success
            document.body.classList.remove('modal-open') // Unlocks scrolling
            Turbo.visit(membersCards.activated({ card_id: cardId }), { frame: 'modal', action: 'advance' })
          } else {
            // TODO: Replace alert with an actually nice way of displaying errors from Unit's API.
            alert(data.errors[0].title)
            enableSubmit()
          }
        },
        function (errors: any[]) {
          highlightErrors(targetForm, errors)
        }
      )
    }

    const disableSubmit = function () {
      const submitButton = document.getElementById('set-pin-submit') as HTMLButtonElement
      submitButton.disabled = true
      submitButton.textContent = 'Setting PIN...'
    }

    const enableSubmit = function () {
      const submitButton = document.getElementById('set-pin-submit') as HTMLButtonElement
      submitButton.disabled = false
      submitButton.textContent = 'Set PIN'
    }

    // Submit by executing a POST request.
    document.getElementById('cc-form')!
      .addEventListener('submit', function (e) {
        e.preventDefault()
        const targetForm = e.target as HTMLFormElement
        disableSubmit()
        submitForm(form, targetForm)
      }, false)
  }

  close () {
    Promise.all([leave(this.closeTarget), leave(this.bodyTarget)]).then(() => {
      document.body.classList.remove('modal-open')
      Turbo.visit(membersCards.index(), { frame: 'modal' })
    })
  }
}
